import { useState, useMemo } from 'react';
import { SEO, BreadcrumbSchema, SpeakableSchema, FaqSchema } from '@/components/SEO';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { CTABanner } from '@/components/CTABanner';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, X, ChevronLeft, ChevronRight, MapPin, Camera } from 'lucide-react';

import deckpro1 from '@/assets/deckpro (1).jpeg';
import deckpro2 from '@/assets/deckpro (2).jpeg';
import deckpro3 from '@/assets/deckpro (3).jpeg';
import deckpro4 from '@/assets/deckpro (4).jpeg';
import deckpro5 from '@/assets/deckpro (5).jpeg';
import deckpro6 from '@/assets/deckpro (6).jpeg';
import deckpro7 from '@/assets/deckpro (7).jpeg';
import deckpro8 from '@/assets/deckpro (8).jpeg';
import deckpro9 from '@/assets/deckpro (9).jpeg';
import deckpro10 from '@/assets/deckpro (10).jpeg';
import deckpro11 from '@/assets/deckpro (11).jpeg';
import deckpro12 from '@/assets/deckpro (12).jpeg';
import deckpro13 from '@/assets/deckpro (13).jpeg';
import deckpro14 from '@/assets/deckpro (14).jpeg';
import deckpro15 from '@/assets/deckpro (15).jpeg';
import deckpro16 from '@/assets/deckpro (16).jpeg';
import deckpro17 from '@/assets/deckpro (17).jpeg';
import deckpro18 from '@/assets/deckpro (18).jpeg';
import deckpro19 from '@/assets/deckpro (19).jpeg';
import deckpro20 from '@/assets/deckpro (20).jpeg';
import deckpro21 from '@/assets/deckpro (21).jpeg';
import deckpro22 from '@/assets/deckpro (22).jpeg';
import deckpro23 from '@/assets/deckpro (23).jpeg';
import deckpro24 from '@/assets/deckpro (24).jpeg';
import deckpro25 from '@/assets/deckpro (25).jpeg';
import deckpro26 from '@/assets/deckpro (26).jpeg';
import deckpro27 from '@/assets/deckpro (27).jpeg';
import deckpro28 from '@/assets/deckpro (28).jpeg';
import deckpro29 from '@/assets/deckpro (29).jpeg';
import deckpro30 from '@/assets/deckpro (30).jpeg';
import deckpro31 from '@/assets/deckpro (31).jpeg';
import deckpro32 from '@/assets/deckpro (32).jpeg';
import deckpro33 from '@/assets/deckpro (33).jpeg';
import deckpro34 from '@/assets/deckpro (34).jpeg';
import deckpro35 from '@/assets/deckpro (35).jpeg';
import deckpro36 from '@/assets/deckpro (36).jpeg';
import deckpro37 from '@/assets/deckpro (37).jpeg';
import deckpro38 from '@/assets/deckpro (38).jpeg';
import deckpro39 from '@/assets/deckpro (39).jpeg';
import deckpro40 from '@/assets/deckpro (40).jpeg';
import deckpro41 from '@/assets/deckpro (41).jpeg';
import deckpro42 from '@/assets/deckpro (42).jpeg';

/* ─── Gallery data ──────────────────────────────────── */
type Category = 'all' | 'wood' | 'composite' | 'multi-level';

interface GalleryImage {
  url: string;
  category: Exclude<Category, 'all'>;
  alt: string;
  location: string;
}

const galleryImages: GalleryImage[] = [
  // Project 1 — pressure-treated wood with lattice (Morris County)
  { url: deckpro1, category: 'wood', alt: 'Pressure-treated wood deck with lattice skirting in Morris County, NJ', location: 'Morris County, NJ' },
  { url: deckpro2, category: 'wood', alt: 'Wood deck stairs and railing detail built in New Jersey', location: 'Morris County, NJ' },
  { url: deckpro3, category: 'wood', alt: 'Backyard wood deck with cable railings in NJ', location: 'Morris County, NJ' },
  { url: deckpro4, category: 'wood', alt: 'Pressure-treated deck with lattice detail, DeckPros NJ build', location: 'Morris County, NJ' },
  { url: deckpro5, category: 'wood', alt: 'Custom wood deck with staircase in Morris County', location: 'Morris County, NJ' },
  { url: deckpro6, category: 'wood', alt: 'Wood deck finishing details by DeckPros NJ', location: 'Morris County, NJ' },
  { url: deckpro7, category: 'wood', alt: 'Pressure-treated wood deck build, New Jersey', location: 'Morris County, NJ' },
  { url: deckpro8, category: 'wood', alt: 'Deck framing and wood boards, DeckPros NJ', location: 'Morris County, NJ' },
  // Project 2 — multi-level composite with under-deck living (Sussex County)
  { url: deckpro9, category: 'multi-level', alt: 'Multi-level white composite deck with under-deck living space', location: 'Sussex County, NJ' },
  { url: deckpro10, category: 'multi-level', alt: 'White composite deck with covered lower area in NJ', location: 'Sussex County, NJ' },
  { url: deckpro11, category: 'multi-level', alt: 'Under-deck storage and composite ceiling in Sussex County', location: 'Sussex County, NJ' },
  { url: deckpro12, category: 'multi-level', alt: 'Multi-level deck stairs in white composite', location: 'Sussex County, NJ' },
  { url: deckpro13, category: 'composite', alt: 'Composite deck with white railings, NJ install', location: 'Sussex County, NJ' },
  { url: deckpro14, category: 'composite', alt: 'Composite decking install, New Jersey', location: 'Sussex County, NJ' },
  { url: deckpro15, category: 'composite', alt: 'Custom deck design by DeckPros NJ', location: 'Sussex County, NJ' },
  { url: deckpro16, category: 'multi-level', alt: 'Multi-level deck with built-in seating, NJ', location: 'Sussex County, NJ' },
  { url: deckpro17, category: 'composite', alt: 'Composite decking boards close-up, NJ', location: 'Sussex County, NJ' },
  { url: deckpro18, category: 'multi-level', alt: 'Multi-level composite deck with white railings', location: 'Sussex County, NJ' },
  { url: deckpro19, category: 'composite', alt: 'White composite decking with metal railings in NJ', location: 'Sussex County, NJ' },
  { url: deckpro20, category: 'multi-level', alt: 'Under-deck living and covered patio area in NJ', location: 'Sussex County, NJ' },
  { url: deckpro21, category: 'composite', alt: 'Premium composite deck build, DeckPros NJ', location: 'Sussex County, NJ' },
  { url: deckpro22, category: 'composite', alt: 'Composite decking and railings installed in NJ', location: 'Sussex County, NJ' },
  { url: deckpro23, category: 'multi-level', alt: 'Multi-level deck with covered storage in Sussex County', location: 'Sussex County, NJ' },
  // Project 3 — premium composite architectural (Somerset County)
  { url: deckpro24, category: 'composite', alt: 'Premium gray composite deck with architectural details', location: 'Somerset County, NJ' },
  { url: deckpro25, category: 'composite', alt: 'Modern gray composite deck in Somerset County, NJ', location: 'Somerset County, NJ' },
  { url: deckpro26, category: 'composite', alt: 'Architectural composite deck with home integration', location: 'Somerset County, NJ' },
  { url: deckpro27, category: 'composite', alt: 'Trex/TimberTech composite install by DeckPros NJ', location: 'Somerset County, NJ' },
  { url: deckpro28, category: 'composite', alt: 'Premium composite deck detail, New Jersey', location: 'Somerset County, NJ' },
  { url: deckpro29, category: 'composite', alt: 'Gray composite deck with black railings in NJ', location: 'Somerset County, NJ' },
  { url: deckpro30, category: 'composite', alt: 'Modern composite decking with hidden fasteners, NJ', location: 'Somerset County, NJ' },
  { url: deckpro31, category: 'composite', alt: 'Composite deck stairs and architectural landing', location: 'Somerset County, NJ' },
  { url: deckpro32, category: 'composite', alt: 'Composite deck with outdoor lounge, Somerset County', location: 'Somerset County, NJ' },
  { url: deckpro33, category: 'composite', alt: 'Premium composite boards close-up, NJ', location: 'Somerset County, NJ' },
  { url: deckpro34, category: 'composite', alt: 'Architectural deck home integration, DeckPros NJ', location: 'Somerset County, NJ' },
  { url: deckpro35, category: 'composite', alt: 'Gray composite deck railings detail, NJ', location: 'Somerset County, NJ' },
  { url: deckpro36, category: 'composite', alt: 'Modern deck with integrated lighting, New Jersey', location: 'Somerset County, NJ' },
  { url: deckpro37, category: 'composite', alt: 'Composite deck finishing, DeckPros NJ', location: 'Somerset County, NJ' },
  { url: deckpro38, category: 'composite', alt: 'Architectural composite deck, Somerset County, NJ', location: 'Somerset County, NJ' },
  { url: deckpro39, category: 'composite', alt: 'Gray composite deck build, premium finish', location: 'Somerset County, NJ' },
  { url: deckpro40, category: 'composite', alt: 'Composite decking project, New Jersey', location: 'Somerset County, NJ' },
  { url: deckpro41, category: 'composite', alt: 'Premium deck project complete, DeckPros NJ', location: 'Somerset County, NJ' },
  { url: deckpro42, category: 'composite', alt: 'Architectural composite deck finished, NJ', location: 'Somerset County, NJ' },
];

const filters: { key: Category; label: string; count?: number }[] = [
  { key: 'all', label: 'All Projects' },
  { key: 'wood', label: 'Wood Decks' },
  { key: 'composite', label: 'Composite' },
  { key: 'multi-level', label: 'Multi-Level' },
];

/* ─── Hero ─────────────────────────────────────────── */
const PageHero = () => (
  <section className="relative pt-28 pb-14 bg-navy text-white overflow-hidden">
    <div className="container mx-auto px-4 relative z-10">
      <div className="text-center max-w-4xl mx-auto">
        <p className="section-eyebrow justify-center">Real Projects · Real Homes</p>
        <h1
          className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-4 uppercase leading-[1.05]"
          data-speakable
        >
          Deck <span className="text-lime">Gallery</span>
        </h1>
        <p
          className="text-base md:text-lg text-white/75 max-w-2xl mx-auto leading-relaxed font-body"
          data-speakable
        >
          Over 40 photos from {galleryImages.length} completed deck projects across Morris, Sussex, and Somerset Counties — built by our in-house DeckPros NJ crew.
        </p>
      </div>
    </div>
  </section>
);

/* ─── Featured Projects strip ──────────────────────── */
const FeaturedProjects = () => {
  const navigate = useNavigate();

  const cards = [
    { image: deckpro1, title: 'Pressure-Treated Wood Build', tag: 'Wood', location: 'Morris County' },
    { image: deckpro9, title: 'Multi-Level Composite w/ Under-Deck', tag: 'Multi-Level', location: 'Sussex County' },
    { image: deckpro24, title: 'Premium Architectural Composite', tag: 'Composite', location: 'Somerset County' },
  ];

  return (
    <section className="section-compact bg-cream">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <p className="section-eyebrow justify-center">Featured Builds</p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-3">
            Three Signature <span className="text-lime">Projects</span>
          </h2>
          <p className="text-stone font-body">
            Click any project for the full photo tour and project details.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 max-w-6xl mx-auto">
          {cards.map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              onClick={() => navigate('/projects')}
              className="group cursor-pointer"
            >
              <div className="relative overflow-hidden border-2 border-navy/10 group-hover:border-lime transition-all duration-200 group-hover:-translate-y-1 group-hover:shadow-hard aspect-[4/3]">
                <img src={c.image} alt={c.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/30 to-transparent" />
                <div className="absolute top-3 left-3">
                  <span className="bg-lime text-navy font-display font-bold text-[9px] uppercase tracking-[0.18em] px-2 py-1">
                    {c.tag}
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="font-display font-bold text-white text-base md:text-lg uppercase tracking-wide leading-tight mb-1">
                    {c.title}
                  </h3>
                  <div className="flex items-center gap-3 text-white/75 text-xs font-body">
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-lime" />{c.location}</span>
                    <span className="flex items-center gap-1 text-lime font-display font-bold uppercase tracking-wider">
                      View Project <ChevronRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Masonry grid with filter + lightbox ─────────── */
const MasonryGallery = () => {
  const [filter, setFilter] = useState<Category>('all');
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const filtered = useMemo(() => {
    return filter === 'all' ? galleryImages : galleryImages.filter(img => img.category === filter);
  }, [filter]);

  const close = () => setSelectedIndex(null);
  const prev = () => setSelectedIndex((i) => (i === null ? null : (i - 1 + filtered.length) % filtered.length));
  const next = () => setSelectedIndex((i) => (i === null ? null : (i + 1) % filtered.length));

  const countFor = (key: Category) =>
    key === 'all' ? galleryImages.length : galleryImages.filter(img => img.category === key).length;

  return (
    <section className="section-compact bg-cream-dark" itemScope itemType="https://schema.org/ImageGallery">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8 max-w-2xl mx-auto">
          <p className="section-eyebrow justify-center">Full Portfolio</p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-3">
            Browse <span className="text-lime">{galleryImages.length} Photos</span>
          </h2>
          <p className="text-stone font-body">
            Filter by material, click any photo to view full-size.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {filters.map((f) => {
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 font-display uppercase text-xs font-bold tracking-[0.15em] border-2 transition-all ${
                  active
                    ? 'bg-lime border-navy text-navy shadow-hard'
                    : 'bg-white border-navy text-navy hover:bg-lime hover:border-navy'
                }`}
              >
                {f.label}
                <span className={`text-[10px] ${active ? 'text-navy/70' : 'text-stone/60'}`}>
                  ({countFor(f.key)})
                </span>
              </button>
            );
          })}
        </div>

        {/* Masonry grid */}
        <div className="columns-2 md:columns-3 lg:columns-4 gap-3 max-w-[1500px] mx-auto">
          {filtered.map((img, i) => (
            <button
              key={img.url}
              onClick={() => setSelectedIndex(i)}
              className="group mb-3 block w-full break-inside-avoid relative overflow-hidden border-2 border-navy/10 hover:border-lime transition-all duration-200 hover:shadow-hard"
              aria-label={`View photo: ${img.alt}`}
            >
              <meta itemProp="associatedMedia" itemScope itemType="https://schema.org/ImageObject" />
              <img
                src={img.url}
                alt={img.alt}
                loading="lazy"
                className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
                itemProp="contentUrl"
              />
              <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/40 transition-colors duration-200 flex items-end">
                <div className="translate-y-full group-hover:translate-y-0 transition-transform duration-200 p-3 w-full">
                  <div className="flex items-center justify-between text-white">
                    <span className="flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-wider">
                      <MapPin className="w-3 h-3 text-lime" />
                      {img.location}
                    </span>
                    <span className="flex items-center gap-1 text-lime text-[10px] font-display font-bold uppercase tracking-wider">
                      <Camera className="w-3 h-3" />
                      View
                    </span>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <Dialog open={selectedIndex !== null} onOpenChange={(open) => !open && close()}>
        <DialogContent className="max-w-5xl p-0 bg-navy border-2 border-lime rounded-none [&>button]:hidden">
          {selectedIndex !== null && (
            <div className="relative">
              <img
                src={filtered[selectedIndex].url}
                alt={filtered[selectedIndex].alt}
                className="w-full max-h-[80vh] object-contain bg-navy"
              />

              <button
                onClick={close}
                aria-label="Close"
                className="absolute top-3 right-3 w-10 h-10 bg-navy border-2 border-lime text-lime hover:bg-lime hover:text-navy flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              <button
                onClick={prev}
                aria-label="Previous"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-12 h-12 bg-navy/80 border-2 border-lime text-lime hover:bg-lime hover:text-navy flex items-center justify-center transition-colors"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              <button
                onClick={next}
                aria-label="Next"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-12 h-12 bg-navy/80 border-2 border-lime text-lime hover:bg-lime hover:text-navy flex items-center justify-center transition-colors"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              <div className="bg-navy border-t-2 border-lime px-5 py-4 text-white">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-display text-sm font-bold text-lime uppercase tracking-wide">
                      {filtered[selectedIndex].category} Deck
                    </p>
                    <p className="text-xs text-white/80 font-body mt-0.5 flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-lime" />
                      {filtered[selectedIndex].location}
                    </p>
                  </div>
                  <span className="text-[10px] text-white/60 font-display uppercase tracking-[0.2em]">
                    Photo {selectedIndex + 1} of {filtered.length}
                  </span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

/* ─── View Projects CTA strip ──────────────────────── */
const ProjectsCTA = () => (
  <section className="bg-lime border-y-[6px] border-navy py-8 md:py-10">
    <div className="container mx-auto px-4">
      <div className="flex flex-col md:flex-row items-center justify-between gap-5 max-w-5xl mx-auto text-center md:text-left">
        <div>
          <p className="font-display text-[11px] uppercase tracking-[0.24em] text-navy/80 mb-1 font-bold">
            Want the full story?
          </p>
          <h3 className="font-display text-2xl md:text-3xl font-bold text-navy uppercase leading-tight">
            See Each Project in <span className="text-white" style={{ textShadow: '2px 2px 0 hsl(var(--navy))' }}>Detail</span>
          </h3>
        </div>
        <Link to="/projects">
          <Button variant="default" size="lg">
            View Project Details
            <ArrowRight className="w-4 h-4" />
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

/* ─── FAQ (AEO / GEO) ──────────────────────────────── */
const galleryFaqs = [
  {
    question: 'What types of decks are shown in the DeckPros NJ gallery?',
    answer: 'The DeckPros NJ gallery features over 40 photos from three signature projects: a pressure-treated wood deck with lattice detail in Morris County, a multi-level composite deck with under-deck living space in Sussex County, and a premium architectural composite deck in Somerset County.',
  },
  {
    question: 'Are these photos of real DeckPros NJ projects?',
    answer: 'Yes — every photo in our gallery is from an actual deck built by DeckPros NJ\'s in-house crew in New Jersey. We do not use stock photography. All projects were completed in Morris, Sussex, or Somerset County.',
  },
  {
    question: 'What deck materials has DeckPros NJ worked with?',
    answer: 'Our portfolio includes pressure-treated wood, premium composite decking (Trex, TimberTech, Azek, Fiberon), PVC decking, and exotic hardwoods like Ipe and Cumaru. We also build multi-level decks and full outdoor living spaces.',
  },
  {
    question: 'Can I request a deck design similar to one in the gallery?',
    answer: 'Absolutely. During your free on-site consultation, you can reference any photo from the gallery and we\'ll design a custom version tailored to your home, budget, and NJ building codes.',
  },
  {
    question: 'Where were these decks built?',
    answer: 'The featured projects were built across Morris County, Sussex County, and Somerset County in New Jersey. DeckPros NJ also serves Warren, Hunterdon, and surrounding northern and central NJ counties.',
  },
];

const FAQSection = () => (
  <section className="section-compact bg-cream">
    <div className="container mx-auto px-4">
      <div className="text-center mb-10 max-w-2xl mx-auto">
        <p className="section-eyebrow justify-center">About the Gallery</p>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-3">
          Portfolio <span className="text-lime">Questions</span>
        </h2>
      </div>

      <div
        className="max-w-4xl mx-auto space-y-3"
        itemScope
        itemType="https://schema.org/FAQPage"
      >
        {galleryFaqs.map((faq, i) => (
          <details
            key={i}
            itemScope
            itemProp="mainEntity"
            itemType="https://schema.org/Question"
            className="group bg-white border-2 border-navy/15 hover:border-lime transition-colors [&[open]]:border-lime [&[open]]:shadow-hard"
          >
            <summary className="flex items-center justify-between gap-4 p-5 cursor-pointer list-none">
              <h3
                itemProp="name"
                className="font-display text-sm md:text-base font-bold text-navy uppercase tracking-wide group-open:text-lime transition-colors"
              >
                {faq.question}
              </h3>
              <span className="font-display text-2xl text-lime leading-none transition-transform group-open:rotate-45 flex-shrink-0">+</span>
            </summary>
            <div
              itemScope
              itemProp="acceptedAnswer"
              itemType="https://schema.org/Answer"
              className="px-5 pb-5 -mt-1"
            >
              <p itemProp="text" className="text-stone text-sm leading-relaxed font-body border-t border-navy/10 pt-4">
                {faq.answer}
              </p>
            </div>
          </details>
        ))}
      </div>
    </div>
  </section>
);

/* ─── ImageGallery / CreativeWork schema ──────────── */
const GallerySchema = () => {
  const base = 'https://deckprosnj.com';
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ImageGallery',
    name: 'DeckPros NJ Project Gallery',
    description: `Photo gallery of ${galleryImages.length} completed deck projects by DeckPros NJ in Morris, Sussex, and Somerset Counties, New Jersey.`,
    url: `${base}/gallery`,
    numberOfItems: galleryImages.length,
    associatedMedia: galleryImages.slice(0, 15).map((img) => ({
      '@type': 'ImageObject',
      contentUrl: img.url.startsWith('http') ? img.url : `${base}${img.url}`,
      description: img.alt,
      contentLocation: img.location,
      creditText: 'DeckPros NJ',
      creator: { '@type': 'Organization', name: 'DeckPros NJ' },
    })),
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>;
};

/* ─── Page ────────────────────────────────────────── */
const Gallery = () => {
  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Deck Gallery · NJ Project Photos"
        description="Browse 40+ real deck photos from DeckPros NJ — pressure-treated wood, composite, PVC, and multi-level decks built in Morris, Sussex, and Somerset Counties, New Jersey. Filter and view full-size."
        canonical="/gallery"
        keywords="deck gallery NJ, New Jersey deck photos, composite deck examples, wood deck photos NJ, multi-level deck portfolio, DeckPros NJ projects, deck inspiration NJ"
      />
      <BreadcrumbSchema items={[
        { name: 'Home', url: '/' },
        { name: 'Gallery', url: '/gallery' },
      ]} />
      <SpeakableSchema />
      <FaqSchema faqs={galleryFaqs} />
      <GallerySchema />

      <Header />
      <main>
        <PageHero />
        <FeaturedProjects />
        <MasonryGallery />
        <ProjectsCTA />
        <FAQSection />
        <CTABanner />
      </main>
      <Footer />
    </div>
  );
};

export default Gallery;
